import { collection, onSnapshot, query, where } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../firebase";
import { normalizeEventRecord } from "../eventViewModel";
import {
  NOVEMBER_CAREERS_SURVEY,
  NOVEMBER_HOUSING_DEVELOPMENT_EVENT,
} from "./config";
import {
  cleanHousingDevelopmentValue,
  isHousingDevelopmentEvent,
  isPublicHousingDevelopmentSurvey,
  mergeMissingSeedRecords,
  normaliseHousingDevelopmentSource,
  normaliseHousingSurveyAnswers,
  surveySlugFromRecord,
  validateHousingSurveyAnswers,
} from "./housingDevelopmentUtils";

export const SUBMIT_HOUSING_DEVELOPMENT_SURVEY_CALLABLE =
  "submitHousingDevelopmentSurveyResponse";

export function includeHousingDevelopmentSeeds() {
  return process.env.NODE_ENV !== "production";
}

export function getLocalHousingDevelopmentEventFallback(
  eventId,
  { includeSeeds = includeHousingDevelopmentSeeds() } = {}
) {
  if (!includeSeeds) return null;
  return cleanHousingDevelopmentValue(eventId) ===
    NOVEMBER_HOUSING_DEVELOPMENT_EVENT.id
    ? { ...NOVEMBER_HOUSING_DEVELOPMENT_EVENT }
    : null;
}

export function normaliseHousingDevelopmentSurveyRecord(id, data = {}) {
  const record = { ...data, id: cleanHousingDevelopmentValue(id || data.id) };
  return {
    ...record,
    surveyId: cleanHousingDevelopmentValue(record.surveyId || record.id),
    slug: surveySlugFromRecord(record),
    surveySlug: surveySlugFromRecord(record),
    questions: Array.isArray(record.questions) ? record.questions : [],
    partnerOrganisations: Array.isArray(record.partnerOrganisations)
      ? record.partnerOrganisations
      : [],
  };
}

export function subscribeToHousingDevelopmentEvents(
  onData,
  onError,
  { includeSeeds = includeHousingDevelopmentSeeds() } = {}
) {
  const approvedEvents = query(
    collection(db, "events"),
    where("status", "==", "approved")
  );

  return onSnapshot(
    approvedEvents,
    (snapshot) => {
      const records = snapshot.docs
        .map((item) => normalizeEventRecord(item.id, item.data()))
        .filter(isHousingDevelopmentEvent);
      onData(
        mergeMissingSeedRecords(
          records,
          [NOVEMBER_HOUSING_DEVELOPMENT_EVENT],
          includeSeeds
        )
      );
    },
    onError
  );
}

export function subscribeToHousingDevelopmentSurveys(
  onData,
  onError,
  { includeSeeds = includeHousingDevelopmentSeeds() } = {}
) {
  const activeSurveys = query(
    collection(db, "surveys"),
    where("programme", "==", "housing-development"),
    where("active", "==", true),
    where("published", "==", true),
    where("status", "==", "published")
  );

  return onSnapshot(
    activeSurveys,
    (snapshot) => {
      const records = snapshot.docs
        .map((item) =>
          normaliseHousingDevelopmentSurveyRecord(item.id, item.data())
        )
        .filter(isPublicHousingDevelopmentSurvey);
      onData(
        mergeMissingSeedRecords(
          records,
          [NOVEMBER_CAREERS_SURVEY],
          includeSeeds
        )
      );
    },
    onError
  );
}

export function buildHousingDevelopmentSurveyPayload({
  survey,
  answers,
  source,
  submissionId,
  honeypot = "",
}) {
  const surveyId = cleanHousingDevelopmentValue(survey?.id || survey?.surveyId);
  const canonicalSubmissionId = cleanHousingDevelopmentValue(submissionId);
  const validationErrors = validateHousingSurveyAnswers(survey, answers);

  if (
    !surveyId ||
    !canonicalSubmissionId ||
    canonicalSubmissionId.length > 128
  ) {
    throw new Error("HOUSING_DEVELOPMENT_SURVEY_CONTEXT_INVALID");
  }
  if (Object.keys(validationErrors).length > 0) {
    const error = new Error("HOUSING_DEVELOPMENT_SURVEY_INVALID");
    error.validationErrors = validationErrors;
    throw error;
  }

  return {
    surveyId,
    submissionId: canonicalSubmissionId,
    submissionSource: normaliseHousingDevelopmentSource(source),
    answers: normaliseHousingSurveyAnswers(survey, answers),
    honeypot: cleanHousingDevelopmentValue(honeypot),
  };
}

export function createHousingSurveySubmissionId() {
  const cryptoApi = typeof window !== "undefined" ? window.crypto : null;
  if (typeof cryptoApi?.randomUUID === "function") {
    return cryptoApi.randomUUID();
  }
  if (typeof cryptoApi?.getRandomValues !== "function") {
    throw new Error("SECURE_RANDOM_UNAVAILABLE");
  }

  const bytes = new Uint8Array(16);
  cryptoApi.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = [...bytes].map((value) => value.toString(16).padStart(2, "0"));
  return [
    hex.slice(0, 4).join(""),
    hex.slice(4, 6).join(""),
    hex.slice(6, 8).join(""),
    hex.slice(8, 10).join(""),
    hex.slice(10).join(""),
  ].join("-");
}

export async function submitHousingDevelopmentSurveyResponse(input) {
  const payload = buildHousingDevelopmentSurveyPayload(input);
  const submit = httpsCallable(
    functions,
    SUBMIT_HOUSING_DEVELOPMENT_SURVEY_CALLABLE
  );
  const result = await submit(payload);
  return result?.data;
}

